import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Formik, Field, FieldArray, Form } from "formik";
import tw from "tailwind-styled-components";
import { MdClose, MdEdit, MdAdd } from "react-icons/md";
import { FaRegCalendar } from "react-icons/fa";
import {
    ModalHeader,
    ActionSection,
    SideCancelBtn,
    SubmitBtn,
    InputGroup,
    FieldWrappers,
    Label,
    Wrapper,
    ScrollableContent,
} from "../../Styles/InputStyles";
import { getFoodUnitLabel } from "../../../helpers";
import SelectOptionModal from "../../SelectOptionModal";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import DropZone from "../../Notice/Dropzone";
import moment from "moment";
import { useSelector } from "react-redux";

const AddPurchase = ({
    handleClose,
    itemList = [],
    addPurchaseMutate,
    addPurchaseLoading = false,
    user
}) => {
    const [height, setHeight] = useState(window.innerHeight);
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const [isVendorModalOpen, setIsVendorModalOpen] = useState(false);
    const [currentItemIndex, setCurrentItemIndex] = useState(0);
    const [calendarOpen, setCalendarOpen] = useState(false);

    // Get vendors from Redux
    const vendors = useSelector((state) => state.VendorTypesReducer.vendors);

    // Filter out items with zero stock or inactive
    const availableItems = useMemo(() =>
        itemList.filter(item => item.status === 1),
        [itemList]
    );

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const initialValues = {
        vendorId: "",
        vendorName: "",
        invoiceNo: "",
        description: "",
        purchaseDate: moment().format("YYYY-MM-DD"),
        entries: [
            {
                itemId: "",
                quantity: 1,
                price: 0,
            }
        ],
        file: null,
    };

    // Get item name by id
    const getItemName = useCallback((id) => {
        if (!id) return "Select Item";
        const item = availableItems.find(i => i.id === id);
        if (item) {
            return `${item.name} (${getFoodUnitLabel(item.unitType)})`;
        }
        return "Select Item";
    }, [availableItems]);

    // Get item details for display
    const getItemDetails = useCallback((id) => {
        if (!id) return null;
        return availableItems.find(i => i.id === id);
    }, [availableItems]);

    // Get vendor name by id
    const getVendorName = useCallback((id) => {
        if (!id) return "Select Vendor";
        const vendor = vendors?.find(v => v.id === id);
        return vendor ? vendor.name : "Select Vendor";
    }, [vendors]);

    // Create vendor options for SelectOptionModal
    const vendorOptions = useMemo(() => {
        if (!vendors || vendors.length === 0) {
            return [{ label: "No vendors available", value: "" }];
        }
        return vendors.map(vendor => ({
            label: vendor.name,
            value: vendor.id,
        }));
    }, [vendors]);

    // Get available items for current index (excluding already selected items)
    const getAvailableItemOptions = useCallback((currentIndex, entries) => {
        // Get all selected item IDs from other entries
        const selectedItemIds = entries
            .filter((_, index) => index !== currentIndex)
            .map(entry => entry.itemId)
            .filter(id => id); // Remove empty values

        // Filter available items to exclude already selected ones
        const availableItemsForSelection = availableItems.filter(
            item => !selectedItemIds.includes(item.id)
        );

        // If current entry already has a selected item, include it
        const currentItemId = entries[currentIndex]?.itemId;
        if (currentItemId) {
            const currentItem = availableItems.find(item => item.id === currentItemId);
            if (currentItem && !availableItemsForSelection.some(item => item.id === currentItemId)) {
                availableItemsForSelection.unshift(currentItem);
            }
        }

        return availableItemsForSelection.map(item => ({
            label: `${item.name} (${getFoodUnitLabel(item.unitType)})`,
            value: item.id,
        }));
    }, [availableItems]);

    const validate = (values) => {
        const errors = {};

        if (!values.vendorId) {
            errors.vendorId = "Vendor selection is required";
        }

        if (!values.purchaseDate) {
            errors.purchaseDate = "Purchase date is required";
        }

        if (!values.entries || values.entries.length === 0) {
            errors.entries = "Please add at least one item";
            return errors;
        }

        // Check for duplicate items
        const itemIds = values.entries
            .filter(entry => entry.itemId)
            .map(entry => entry.itemId);

        const duplicateItems = itemIds.filter((id, index) => itemIds.indexOf(id) !== index);
        if (duplicateItems.length > 0) {
            const duplicateError = "Duplicate items found. Please remove duplicates.";
            // Add error to first duplicate entry
            const firstDuplicateIndex = itemIds.findIndex(id => id === duplicateItems[0]);
            if (firstDuplicateIndex !== -1) {
                const entriesErrors = [];
                entriesErrors[firstDuplicateIndex] = { itemId: duplicateError };
                errors.entries = entriesErrors;
                return errors;
            }
        }

        const entriesErrors = [];
        let hasEntryError = false;

        values.entries.forEach((entry, index) => {
            const entryError = {};

            if (!entry.itemId) {
                entryError.itemId = "Please select an item";
                hasEntryError = true;
            }

            // Quantity validation: min 1, max 1000
            if (!entry.quantity) {
                entryError.quantity = "Quantity is required";
                hasEntryError = true;
            } else if (entry.quantity < 0.01) {
                entryError.quantity = "Quantity must be greater than 0.01";
                hasEntryError = true;
            } else if (entry.quantity > 10000) {
                entryError.quantity = "Quantity cannot exceed 10000";
                hasEntryError = true;
            }

            // Price validation: min 1, max 10,00,000 (10 lakh)
            if (!entry.price) {
                entryError.price = "Price is required";
                hasEntryError = true;
            } else if (entry.price <= 0) {
                entryError.price = "Price must be greater than 0";
                hasEntryError = true;
            } else if (entry.price > 1000000) {
                entryError.price = "Price cannot exceed ₹10,00,000";
                hasEntryError = true;
            } else if (entry.price.toString().split('.')[1]?.length > 2) {
                entryError.price = "Price can have at most 2 decimal places";
                hasEntryError = true;
            }

            if (Object.keys(entryError).length > 0) {
                entriesErrors[index] = entryError;
            }
        });

        if (hasEntryError) {
            errors.entries = entriesErrors;
        }

        return errors;
    };

    const handleSubmit = async (values, { resetForm, setSubmitting }) => {
        if (!values.entries || values.entries.length === 0) {
            toast.error("Please add at least one item");
            return;
        }

        // Prepare payload - use FormData for multipart/form-data
        const formData = new FormData();
        formData.append("vendorId", values.vendorId);
        formData.append("invoiceNo", values.invoiceNo);
        formData.append("description", values.description || "");
        formData.append("purchaseDate", moment(values.purchaseDate).format("YYYY-MM-DD"));

        // Add entries as JSON string
        const entries = values.entries
            .filter(entry => entry.itemId && entry.quantity > 0 && entry.price > 0)
            .map(entry => ({
                itemId: parseInt(entry.itemId),
                quantity: parseFloat(entry.quantity),
                cost: parseFloat(entry.price),
            }));

        formData.append("items", JSON.stringify(entries));

        // Add file if present
        if (values.file && values.file.length > 0) {
            formData.append("file", values.file[0]);
        }

        // Call the mutation
        addPurchaseMutate(formData);
    };

    const handleItemSelect = (selectedValue, setFieldValue, entries) => {
        if (selectedValue) {
            // Check if this item is already selected in other entries
            const isDuplicate = entries.some((entry, index) =>
                index !== currentItemIndex && entry.itemId === selectedValue
            );

            if (isDuplicate) {
                toast.warning("This item has already been added. Please select a different item.");
                return;
            }

            setFieldValue(`entries.${currentItemIndex}.itemId`, selectedValue);
        }
        setIsItemModalOpen(false);
    };

    const handleVendorSelect = (selectedValue, setFieldValue) => {
        if (selectedValue) {
            setFieldValue("vendorId", selectedValue);
            const vendor = vendors?.find(v => v.id === selectedValue);
            if (vendor) {
                setFieldValue("vendorName", vendor.name);
            }
        } else {
            setFieldValue("vendorId", "");
            setFieldValue("vendorName", "");
        }
        setIsVendorModalOpen(false);
    };

    const handleModalClose = () => {
        handleClose();
    };

    return (
        <Container style={{ height }}>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Add Purchase</h1>
                <button onClick={handleModalClose} type="button">
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <div className="px-6 h-full flex flex-col justify-between">
                <Formik
                    initialValues={initialValues}
                    validate={validate}
                    //onSubmit={handleSubmit}
                    onSubmit={handleSubmit}
                    validateOnChange={true}
                    validateOnBlur={true}
                    enableReinitialize={false}
                >
                    {({ values, setFieldValue, errors, touched, isSubmitting, submitCount }) => (
                        <Form>
                            <ScrollableContent className="max-h-[calc(100vh-120px)]">
                                <Wrapper>
                                    {/* Items Section */}
                                    <InputGroup>
                                        <Label>Items to Purchase *</Label>

                                        <FieldArray name="entries">
                                            {({ push, remove }) => (
                                                <div className="space-y-4">
                                                    {values.entries.map((entry, index) => {
                                                        const itemDetails = getItemDetails(entry.itemId);
                                                        const error = errors.entries?.[index];
                                                        // Get available items for this index
                                                        const itemOptions = getAvailableItemOptions(index, values.entries);

                                                        return (
                                                            <ItemCard key={index}>
                                                                <div className="flex justify-between items-start">
                                                                    <div className="flex-1 space-y-3">
                                                                        {/* Item Selection */}
                                                                        <InputGroup>
                                                                            <Label>Select Item *</Label>
                                                                            <FieldWrappers
                                                                                onClick={() => {
                                                                                    setCurrentItemIndex(index);
                                                                                    setIsItemModalOpen(true);
                                                                                }}
                                                                                className="cursor-pointer"
                                                                            >
                                                                                <Field
                                                                                    type="text"
                                                                                    name={`entries.${index}.itemDisplay`}
                                                                                    readOnly
                                                                                    value={getItemName(entry.itemId)}
                                                                                    className="w-full border-none outline-none bg-transparent cursor-pointer"
                                                                                />
                                                                                <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                                                                            </FieldWrappers>
                                                                            {submitCount > 0 && error?.itemId && (
                                                                                <ErrorText>{error.itemId}</ErrorText>
                                                                            )}
                                                                        </InputGroup>

                                                                        {/* Quantity and Price */}
                                                                        <div className="grid grid-cols-2 gap-4">
                                                                            <InputGroup>
                                                                                <Label htmlFor={`entries.${index}.quantity`}>
                                                                                    Quantity *
                                                                                </Label>
                                                                                <FieldWrappers>
                                                                                    <Field
                                                                                        type="number"
                                                                                        name={`entries.${index}.quantity`}
                                                                                        id={`entries.${index}.quantity`}
                                                                                        // min="1"
                                                                                        // max="1000"
                                                                                        //step="1"
                                                                                        // min="0.01"
                                                                                        max="10000"
                                                                                        step="0.01"
                                                                                        className="w-full"
                                                                                        placeholder="Enter quantity"
                                                                                    />
                                                                                </FieldWrappers>
                                                                                {submitCount > 0 && error?.quantity && (
                                                                                    <ErrorText>{error.quantity}</ErrorText>
                                                                                )}
                                                                            </InputGroup>
                                                                            <InputGroup>
                                                                                <Label htmlFor={`entries.${index}.price`}>
                                                                                    Price (₹) *
                                                                                </Label>
                                                                                <FieldWrappers>
                                                                                    <Field
                                                                                        type="number"
                                                                                        name={`entries.${index}.price`}
                                                                                        id={`entries.${index}.price`}
                                                                                        // min="0.01"
                                                                                        // max="1000000"
                                                                                        step="0.01"
                                                                                        className="w-full"
                                                                                        placeholder="Enter price"
                                                                                    />
                                                                                </FieldWrappers>
                                                                                {submitCount > 0 && error?.price && (
                                                                                    <ErrorText>{error.price}</ErrorText>
                                                                                )}
                                                                            </InputGroup>
                                                                        </div>
                                                                    </div>

                                                                    {/* Remove Button */}
                                                                    {values.entries.length > 1 && (
                                                                        <RemoveButton
                                                                            type="button"
                                                                            onClick={() => remove(index)}
                                                                            aria-label="Remove item"
                                                                        >
                                                                            ×
                                                                        </RemoveButton>
                                                                    )}
                                                                </div>
                                                            </ItemCard>
                                                        );
                                                    })}

                                                    {/* Add Item Button at the bottom */}
                                                    <AddItemButtonWrapper>
                                                        <AddItemButton
                                                            type="button"
                                                            onClick={() => {
                                                                push({ itemId: "", quantity: 1, price: 0 });
                                                            }}
                                                        >
                                                            <MdAdd size={18} />
                                                            Add Another Item
                                                        </AddItemButton>
                                                    </AddItemButtonWrapper>
                                                </div>
                                            )}
                                        </FieldArray>
                                    </InputGroup>

                                    {/* Vendor Selection */}
                                    <InputGroup>
                                        <Label>Vendor *</Label>
                                        <FieldWrappers
                                            onClick={() => {
                                                if (vendors && vendors.length > 0) {
                                                    setIsVendorModalOpen(true);
                                                } else {
                                                    toast.warning("No vendors available. Please add vendors first.");
                                                }
                                            }}
                                            className="cursor-pointer"
                                        >
                                            <Field
                                                type="text"
                                                name="vendorName"
                                                readOnly
                                                value={getVendorName(values.vendorId)}
                                                className="w-full border-none outline-none bg-transparent cursor-pointer"
                                                placeholder="Select Vendor"
                                            />
                                            <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                                        </FieldWrappers>
                                        {errors.vendorId && touched.vendorId && (
                                            <ErrorText>{errors.vendorId}</ErrorText>
                                        )}
                                    </InputGroup>

                                    {/* Purchase Date */}
                                    <InputGroup>
                                        <Label htmlFor="purchaseDate">Purchase Date *</Label>
                                        <FieldWrappers className="relative w-full">
                                            <DatePicker
                                                selected={values.purchaseDate}
                                                onChange={(date) =>
                                                    setFieldValue("purchaseDate", date)
                                                }
                                                //maxDate={new Date()}
                                                dateFormat="yyyy-MM-dd"
                                                className="cursor-pointer w-full"
                                                placeholderText="YYYY-MM-DD"
                                                onClickOutside={() => setCalendarOpen(false)}
                                                onSelect={() => setCalendarOpen(false)}
                                                open={calendarOpen}
                                                onInputClick={() => setCalendarOpen(true)}
                                                popperPlacement="top-end"
                                                showIcon
                                                icon={<FaRegCalendar className="text-gray-400 text-xs mt-0.5" />}
                                                showPopperArrow={false}
                                                onFocus={(e) => e.target.blur()}
                                                wrapperClassName="w-full"
                                                portalId="root-portal"
                                                popperClassName="react-datepicker-popper"
                                            />
                                        </FieldWrappers>
                                        {errors.purchaseDate && touched.purchaseDate && (
                                            <ErrorText>{errors.purchaseDate}</ErrorText>
                                        )}
                                    </InputGroup>

                                    {/* Invoice Number */}
                                    <InputGroup>
                                        <Label htmlFor="invoiceNo">Invoice Number</Label>
                                        <FieldWrappers>
                                            <Field
                                                type="text"
                                                name="invoiceNo"
                                                id="invoiceNo"
                                                placeholder="Enter invoice number"
                                                className="w-full"
                                            />
                                        </FieldWrappers>
                                        {errors.invoiceNo && touched.invoiceNo && (
                                            <ErrorText>{errors.invoiceNo}</ErrorText>
                                        )}
                                    </InputGroup>

                                    {/* Description */}
                                    <InputGroup>
                                        <Label htmlFor="description">Description</Label>
                                        <FieldWrapper>
                                            <Field
                                                as={Textarea}
                                                id="description"
                                                name="description"
                                                rows={4}
                                                maxLength={500}
                                                placeholder="Write your description here..."
                                            />
                                        </FieldWrapper>
                                    </InputGroup>

                                    {/* Document Upload */}
                                    <InputGroup>
                                        <Label htmlFor="file">Document (optional)</Label>
                                        <FieldWrapper $select={true}>
                                            <Field
                                                name="file"
                                                type="text"
                                                id="file"
                                                autoComplete="off"
                                            >
                                                {(props) => (
                                                    <DropZone
                                                        fields={props.field}
                                                        setFieldValue={props.form.setFieldValue}
                                                        componentFor="expense"
                                                        acceptedFiles="image/*,application/pdf"
                                                        File={props.field.value ? [props.field.value] : null}
                                                    />
                                                )}
                                            </Field>
                                        </FieldWrapper>
                                    </InputGroup>
                                </Wrapper>
                            </ScrollableContent>

                            <ActionSection>
                                <SideCancelBtn type="button" onClick={handleModalClose}>
                                    Cancel
                                </SideCancelBtn>
                                <SubmitBtn
                                    type="submit"
                                    disabled={isSubmitting || addPurchaseLoading}
                                >
                                    {isSubmitting || addPurchaseLoading ? "Submitting..." : "Submit"}
                                </SubmitBtn>
                            </ActionSection>

                            {/* Item Selection Modal */}
                            {isItemModalOpen && (
                                <SelectOptionModal
                                    isOpen={isItemModalOpen}
                                    onClose={() => setIsItemModalOpen(false)}
                                    title="Select Item"
                                    options={getAvailableItemOptions(currentItemIndex, values.entries)}
                                    selectedValue={values.entries[currentItemIndex]?.itemId}
                                    onSelect={(value) => handleItemSelect(value, setFieldValue, values.entries)}
                                    searchPlaceholder="Search items..."
                                />
                            )}

                            {/* Vendor Selection Modal */}
                            <SelectOptionModal
                                isOpen={isVendorModalOpen}
                                onClose={() => setIsVendorModalOpen(false)}
                                title="Select Vendor"
                                options={vendorOptions}
                                selectedValue={values.vendorId}
                                onSelect={(value) => handleVendorSelect(value, setFieldValue)}
                                searchPlaceholder="Search vendors..."
                            />
                        </Form>
                    )}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5 mt-1`;
const ItemCard = tw.div`bg-gray-50 p-4 rounded-lg border border-gray-200 relative`;
const AddItemButtonWrapper = tw.div`flex justify-center mt-2`;
const AddItemButton = tw.button`
    flex items-center gap-2 
    text-sm text-primary-color font-medium 
    hover:text-primary-color/80 
    px-4 py-2 
    border border-dashed border-primary-color/30 
    rounded-lg 
    hover:border-primary-color 
    hover:bg-primary-color/5 
    transition-all duration-200
`;
const RemoveButton = tw.button`
    text-gray-400 hover:text-red-500 
    text-xl ml-2 p-1 
    transition-colors duration-200
`;
const FieldWrapper = tw.div`w-full`;
const Textarea = tw.textarea`
    min-h-30 border border-gray-300 
    rounded pl-2 py-2 
    focus:ring-1 focus:ring-sky-600 
    focus:border-sky-600 focus:outline-none 
    duration-200 w-full h-30 
    resize-y break-words overflow-hidden
`;

export default AddPurchase;